<template>
  <layout-basic>
    <page-hero
      title="How IMPROVE Helps"
      lead="Everything You Need to Train Your Workforce — powerful features that make learning simple for managers and engaging for employees."
    />

    <section class="demo-video-section">
      <v-container style="max-width: 960px">
        <demo-video video-key="features" />
      </v-container>
    </section>

    <section class="feature-cards-section">
      <v-container>
        <h2 class="font-sora feature-cards__title">Built around business outcomes</h2>
        <div class="feature-cards">
          <article v-for="card in cards" :key="card.title" class="feature-card">
            <h3 class="font-sora">{{ card.title }}</h3>
            <p>{{ card.body }}</p>
          </article>
        </div>
      </v-container>
    </section>

    <point-grid
      title="What managers and employees get"
      :points="points"
      :columns="2"
      tint
    />

    <standard-cta
      title="See every feature in action."
      lead="Watch the demo or experience IMPROVE and explore the platform yourself."
    />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PageHero from '@/components/marketing/PageHero.vue';
import PointGrid from '@/components/marketing/PointGrid.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import DemoVideo from '@/components/DemoVideo.vue';
import { marketingPageMeta } from '@/utils/marketingPage';

export default {
  name: 'Features',
  components: {
    LayoutBasic,
    PageHero,
    PointGrid,
    StandardCta,
    DemoVideo,
  },
  metaInfo() {
    return marketingPageMeta({
      title: 'How IMPROVE Helps | Everything You Need to Train Your Workforce',
      description:
        'Faster onboarding, compliance reporting, learning pathways, your own content, reporting dashboards and learn-anywhere access.',
      path: '/features',
    });
  },
  data() {
    return {
      cards: [
        {
          title: 'Faster Onboarding',
          body: 'Get new starters productive sooner with structured pathways and consistent day-one learning.',
        },
        {
          title: 'Compliance Reporting',
          body: 'Know who has completed mandatory training and pull clear evidence when you need it.',
        },
        {
          title: 'Learning Pathways',
          body: 'Guide employees through role-based development without complex course administration.',
        },
        {
          title: 'Upload Your Own Content',
          body: 'Add policies, procedures, videos and company training alongside the IMPROVE library.',
        },
        {
          title: 'Reporting Dashboard',
          body: 'See progress across teams in one place so managers spend less time chasing completions.',
        },
        {
          title: 'Learn Anywhere',
          body: 'Give every employee mobile-friendly access to learning whenever and wherever they work.',
        },
      ],
      points: [
        'Simple for managers to assign and track',
        'Engaging enough for employees to complete',
        '130+ courses included with every plan',
        'Designed for growing businesses, not enterprise complexity',
      ],
    };
  },
};
</script>

<style scoped>
.feature-cards-section {
  padding: 24px 0 8px;
}

.feature-cards__title {
  text-align: center;
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 28px;
}

.feature-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  max-width: 980px;
  margin: 0 auto;
}

.feature-card {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 22px 18px;
}

.feature-card h3 {
  color: var(--brand-navy, #002458);
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 10px;
}

.feature-card p {
  color: var(--brand-grey, #6b7280);
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}

@media screen and (max-width: 900px) {
  .feature-cards {
    grid-template-columns: 1fr;
  }
}
</style>
